import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  TextField,
  Card,
  CardContent,
  Collapse,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import { format, subDays, startOfMonth, startOfWeek, isSunday, getHours, isValid } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#00838f', '#c2185b', '#455a64', '#e64a19', '#1565c0',
  '#2e7d32', '#ad1457', '#4527a0', '#00695c', '#ef6c00'
];

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

// Helper to create a date at a specific time (assumes local machine is in Eastern timezone or similar)
const createEasternTime = (date, hours, minutes = 0) => {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

// Get today's default shift times (6:00 PM to 2:00 AM next day)
const getDefaultShiftTimes = () => {
  const today = new Date();
  const startTime = createEasternTime(today, 18, 0); // 6:00 PM
  const endTime = createEasternTime(today, 2, 0); // 2:00 AM
  endTime.setDate(endTime.getDate() + 1); // Next day
  return { startTime, endTime };
};

function Reports() {
  // Date/time selection state - default to typical shift hours
  const defaultTimes = getDefaultShiftTimes();
  const [startDate, setStartDate] = useState(defaultTimes.startTime);
  const [endDate, setEndDate] = useState(defaultTimes.endTime);
  const [activeQuickFilter, setActiveQuickFilter] = useState('today');

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [summary, setSummary] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [associateData, setAssociateData] = useState([]);
  const [associateCategoryData, setAssociateCategoryData] = useState([]);
  const [putawayData, setPutawayData] = useState({ sessions: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sort state
  const [sortColumn, setSortColumn] = useState('totalLines');
  const [sortDirection, setSortDirection] = useState('desc');

  // Expanded rows for associate-category view
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Daily trend data
  const [dailySummary, setDailySummary] = useState([]);
  const [dailyCategory, setDailyCategory] = useState([]);
  const [dailyAssociate, setDailyAssociate] = useState([]);
  const [highlightedAssociate, setHighlightedAssociate] = useState(null);
  const [highlightedCategory, setHighlightedCategory] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);

  // Quick filter handlers - use shift times (6pm-2am) in Eastern time
  const handleQuickFilter = (filter) => {
    setActiveQuickFilter(filter);
    const today = new Date();

    switch (filter) {
      case 'today':
        // Today's shift: 6pm today to 2am tomorrow
        setStartDate(createEasternTime(today, 18, 0));
        const endToday = createEasternTime(today, 2, 0);
        endToday.setDate(endToday.getDate() + 1);
        setEndDate(endToday);
        break;
      case 'yesterday':
        // Yesterday's shift: 6pm yesterday to 2am today
        const yesterday = subDays(today, 1);
        setStartDate(createEasternTime(yesterday, 18, 0));
        setEndDate(createEasternTime(today, 2, 0));
        break;
      case 'thisWeek': {
        // This week: most recent Sunday at 6 PM to now
        // If today is Sunday before 6 PM, go back to previous Sunday
        let weekStart = startOfWeek(today, { weekStartsOn: 0 }); // Sunday
        if (isSunday(today) && getHours(today) < 18) {
          weekStart = subDays(weekStart, 7);
        }
        setStartDate(createEasternTime(weekStart, 18, 0));
        setEndDate(new Date());
        break;
      }
      case 'last7':
        // Last 7 days: 6pm 7 days ago to 2am tomorrow
        setStartDate(createEasternTime(subDays(today, 6), 18, 0));
        const end7 = createEasternTime(today, 2, 0);
        end7.setDate(end7.getDate() + 1);
        setEndDate(end7);
        break;
      case 'last30':
        // Last 30 days: 6pm 30 days ago to 2am tomorrow
        setStartDate(createEasternTime(subDays(today, 29), 18, 0));
        const end30 = createEasternTime(today, 2, 0);
        end30.setDate(end30.getDate() + 1);
        setEndDate(end30);
        break;
      case 'thisMonth':
        // This month: 6pm on 1st to 2am tomorrow
        setStartDate(createEasternTime(startOfMonth(today), 18, 0));
        const endMonth = createEasternTime(today, 2, 0);
        endMonth.setDate(endMonth.getDate() + 1);
        setEndDate(endMonth);
        break;
      default:
        break;
    }
  };

  // Fetch data when dates change
  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    if (!startDate || !isValid(startDate) || !endDate || !isValid(endDate)) {
      setError('Please enter a valid start and end date/time.');
      setLoading(false);
      return;
    }

    // Format dates as ISO strings - the backend will parse these
    const start = startDate.toISOString();
    const end = endDate.toISOString();

    // Format dates for putaway API (YYYY-MM-DD)
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');

    try {
      // Report endpoints require auth (employee performance data)
      const reportHeaders = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
      const [summaryRes, categoryRes, associateRes, assocCatRes, putawayRes] = await Promise.all([
        fetch(`${API_BASE}/api/obd/reports/summary?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
        fetch(`${API_BASE}/api/obd/reports/by-category?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
        fetch(`${API_BASE}/api/obd/reports/by-associate?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
        fetch(`${API_BASE}/api/obd/reports/by-associate-category?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
        fetch(`${API_BASE}/api/putaway/report?start_date=${startDateStr}&end_date=${endDateStr}`, {
          // Read the JWT from localStorage (same convention as OBDSignOn / ClockIn) —
          // AuthContext does not expose a `token` field, so useAuth().token was undefined,
          // which sent `Bearer undefined` and 401'd, leaving the report perpetually empty.
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        })
      ]);

      if (!summaryRes.ok || !categoryRes.ok || !associateRes.ok || !assocCatRes.ok) {
        throw new Error('Failed to fetch report data');
      }

      const [summaryData, catData, assocData, assocCatData] = await Promise.all([
        summaryRes.json(),
        categoryRes.json(),
        associateRes.json(),
        assocCatRes.json()
      ]);

      setSummary(summaryData);
      setCategoryData(catData);
      setAssociateData(assocData);
      setAssociateCategoryData(assocCatData);

      // Putaway data - handle separately to not break other reports if it fails
      if (putawayRes.ok) {
        const putawayDataResult = await putawayRes.json();
        setPutawayData(putawayDataResult);
      } else {
        // Don't fail invisibly: a 401/404/500 here previously looked like "no data".
        console.error(`Putaway report fetch failed: ${putawayRes.status} ${putawayRes.statusText}`);
      }

      // Fetch daily breakdown data for trendline charts
      try {
        const [dailySumRes, dailyCatRes, dailyAssocRes] = await Promise.all([
          fetch(`${API_BASE}/api/obd/reports/daily-summary?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
          fetch(`${API_BASE}/api/obd/reports/daily-by-category?startDate=${start}&endDate=${end}`, { headers: reportHeaders }),
          fetch(`${API_BASE}/api/obd/reports/daily-by-associate?startDate=${start}&endDate=${end}`, { headers: reportHeaders })
        ]);
        if (dailySumRes.ok) setDailySummary(await dailySumRes.json());
        if (dailyCatRes.ok) setDailyCategory(await dailyCatRes.json());
        if (dailyAssocRes.ok) setDailyAssociate(await dailyAssocRes.json());
      } catch (e) {
        console.warn('Daily trend data fetch failed:', e);
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort data
  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortColumn] || 0;
      const bVal = b[sortColumn] || 0;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Toggle expanded row
  const toggleExpanded = (associate) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(associate)) {
      newExpanded.delete(associate);
    } else {
      newExpanded.add(associate);
    }
    setExpandedRows(newExpanded);
  };

  // CSV Export helpers
  const getDateRangeForFilename = () => {
    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');
    return `${startStr}-to-${endStr}`;
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportBuildingOverview = () => {
    if (!summary) return;

    const headers = ['Total OBDs', 'Total Lines', 'Total Units', 'Total Hours', 'OBDs/Hour', 'Lines/Hour', 'Units/Hour'];
    const values = [
      summary.totalObds,
      summary.totalLines,
      summary.totalUnits,
      summary.totalHours,
      summary.obdsPerHour,
      summary.linesPerHour,
      summary.unitsPerHour
    ];

    const csv = headers.join(',') + '\n' + values.join(',');
    downloadCSV(csv, `report-building-overview-${getDateRangeForFilename()}.csv`);
  };

  const exportCategoryData = () => {
    if (!categoryData.length) return;

    const headers = ['Category', 'OBDs', 'Lines', 'Units', 'Hours', 'OBDs/Hr', 'Lines/Hr', 'Units/Hr'];
    const rows = categoryData.map(row => [
      row.category,
      row.totalObds,
      row.totalLines,
      row.totalUnits,
      row.totalHours,
      row.obdsPerHour,
      row.linesPerHour,
      row.unitsPerHour
    ].join(','));

    const csv = headers.join(',') + '\n' + rows.join('\n');
    downloadCSV(csv, `report-by-category-${getDateRangeForFilename()}.csv`);
  };

  const exportAssociateData = () => {
    if (!associateData.length) return;

    const headers = ['Associate', 'Performance', 'OBDs', 'Lines', 'Units', 'Hours', 'OBDs/Hr', 'Lines/Hr', 'Units/Hr', 'Avg Elapsed (min)', 'Avg Pause (min)', '% OBDs Paused'];
    const rows = associateData.map(row => [
      `"${row.associate}"`,
      row.performance || '',
      row.totalObds,
      row.totalLines,
      row.totalUnits,
      row.totalHours,
      row.obdsPerHour,
      row.linesPerHour,
      row.unitsPerHour,
      row.avgElapsedMinutes != null ? row.avgElapsedMinutes : '',
      row.avgPauseMinutes != null ? row.avgPauseMinutes : '',
      row.pctObdsPaused != null ? row.pctObdsPaused : ''
    ].join(','));

    const csv = headers.join(',') + '\n' + rows.join('\n');
    downloadCSV(csv, `report-by-associate-${getDateRangeForFilename()}.csv`);
  };

  const exportAssociateCategoryData = () => {
    if (!associateCategoryData.length) return;

    const headers = ['Associate', 'Category', 'OBDs', 'Lines', 'Units', 'Hours', 'Lines/Hr'];
    const rows = [];

    associateCategoryData.forEach(assoc => {
      // Add summary row
      rows.push([
        `"${assoc.associate}"`,
        'TOTAL',
        assoc.totalObds,
        assoc.totalLines,
        assoc.totalUnits,
        assoc.totalHours,
        assoc.linesPerHour
      ].join(','));

      // Add category detail rows
      if (assoc.categories) {
        assoc.categories.forEach(cat => {
          rows.push([
            `"${assoc.associate}"`,
            cat.category,
            cat.totalObds,
            cat.totalLines,
            cat.totalUnits,
            cat.totalHours,
            cat.linesPerHour
          ].join(','));
        });
      }
    });

    const csv = headers.join(',') + '\n' + rows.join('\n');
    downloadCSV(csv, `report-associate-by-category-${getDateRangeForFilename()}.csv`);
  };

  // Export putaway data (unified columns, both eras)
  const exportPutawayData = () => {
    if (!putawayData.sessions?.length) return;

    const headers = ['Date', 'Vendor', 'PO', 'People', 'Total Hours'];
    const rows = putawayData.sessions.map(row => [
      row.date,
      `"${row.vendor_name}"`,
      row.po_number || '',
      row.people,
      row.total_hours
    ].join(','));

    const csv = headers.join(',') + '\n' + rows.join('\n');
    downloadCSV(csv, `report-putaway-${getDateRangeForFilename()}.csv`);
  };

  // Get the right export function for current tab
  const handleExport = () => {
    switch (activeTab) {
      case 0:
        exportBuildingOverview();
        break;
      case 1:
        exportCategoryData();
        break;
      case 2:
        exportAssociateData();
        break;
      case 3:
        exportAssociateCategoryData();
        break;
      case 4:
        exportPutawayData();
        break;
      default:
        break;
    }
  };

  // Metric card component
  const MetricCard = ({ title, value, unit, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" color={`${color}.main`} sx={{ fontWeight: 600 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {unit && (
          <Typography variant="caption" color="textSecondary">
            {unit}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Performance chip for associate highlighting
  const PerformanceChip = ({ performance }) => {
    if (performance === 'top') {
      return <Chip size="small" icon={<TrendingUpIcon />} label="Top 25%" color="success" />;
    } else if (performance === 'bottom') {
      return <Chip size="small" icon={<TrendingDownIcon />} label="Bottom 25%" color="error" />;
    }
    return null;
  };

  // Check if timeframe spans more than 1 day
  const isMultiDay = !['today', 'yesterday'].includes(activeQuickFilter) &&
    (endDate - startDate) > 36 * 60 * 60 * 1000; // more than 36 hours

  // Custom tooltip that shows only the highlighted/hovered line, positioned near cursor
  const SingleLineTooltip = ({ active, payload, label, coordinate, highlighted, hovered }) => {
    if (!hovered && !highlighted) return null;
    if (!active || !payload || !payload.length) return null;
    const target = highlighted || hovered;
    const items = target
      ? payload.filter(p => p.name === target)
      : payload.filter(p => p.value != null).slice(0, 1);
    if (!items.length) return null;
    return (
      <div style={{
        position: 'absolute',
        left: (coordinate?.x || 0) + 15,
        top: (coordinate?.y || 0) - 30,
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '8px 12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        pointerEvents: 'none',
        zIndex: 1000,
        fontSize: '0.8rem',
        whiteSpace: 'nowrap'
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {format(new Date(label), 'MMM d, yyyy')}
        </div>
        {items.map(item => (
          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color, display: 'inline-block' }} />
            <span>{item.name}: <strong>{item.value}</strong></span>
          </div>
        ))}
      </div>
    );
  };

  // Render Building Overview
  const renderBuildingOverview = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon color="primary" />
        Building Overview
      </Typography>

      {summary ? (
        <>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="Total OBDs" value={summary.totalObds} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="Total Lines" value={summary.totalLines} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="Total Units" value={summary.totalUnits} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="OBDs/Hour" value={summary.obdsPerHour} color="secondary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="Lines/Hour" value={summary.linesPerHour} color="secondary" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MetricCard title="Units/Hour" value={summary.unitsPerHour} color="secondary" />
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Total Hours Worked
                </Typography>
                <Typography variant="h5" color="info.main" sx={{ fontWeight: 600 }}>
                  {summary.totalHours} hours
                </Typography>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        {/* Daily Trendline Chart — below the stat cards */}
        {isMultiDay && dailySummary.length > 1 && (
          <Paper sx={{ mt: 3, p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Daily Lines per Hour Trend
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={dailySummary}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(val) => format(new Date(val), 'M/d')}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <RechartsTooltip
                  labelFormatter={(val) => format(new Date(val), 'MMM d, yyyy')}
                  formatter={(val) => [val, 'Lines/Hr']}
                />
                <Line
                  type="monotone"
                  dataKey="linesPerHour"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Lines/Hr"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        )}
        </>
      ) : (
        <Typography color="textSecondary">No data available for selected date range</Typography>
      )}
    </Box>
  );

  // Pivot daily category data for multi-series chart
  const categoryChartData = useMemo(() => {
    if (!isMultiDay || dailyCategory.length === 0) return { data: [], categories: [] };
    const dayMap = {};
    const categorySet = new Set();
    dailyCategory.forEach(row => {
      const dateKey = row.date;
      if (!dayMap[dateKey]) dayMap[dateKey] = { date: dateKey };
      dayMap[dateKey][row.category] = row.linesPerHour;
      categorySet.add(row.category);
    });
    return {
      data: Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date)),
      categories: Array.from(categorySet)
    };
  }, [dailyCategory, isMultiDay]);

  // Pivot daily associate data for multi-series chart
  const associateChartData = useMemo(() => {
    if (!isMultiDay || dailyAssociate.length === 0) return { data: [], associates: [] };
    const dayMap = {};
    const assocSet = new Set();
    dailyAssociate.forEach(row => {
      const dateKey = row.date;
      if (!dayMap[dateKey]) dayMap[dateKey] = { date: dateKey };
      dayMap[dateKey][row.associate] = row.linesPerHour;
      assocSet.add(row.associate);
    });
    return {
      data: Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date)),
      associates: Array.from(assocSet)
    };
  }, [dailyAssociate, isMultiDay]);

  // Render Category Performance
  const renderCategoryPerformance = () => {
    const sortedData = sortData(categoryData);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Performance by Category
        </Typography>

        {/* Category Trendline Chart */}
        {isMultiDay && categoryChartData.data.length > 1 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Daily Lines per Hour by Category
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Click a name in the legend to highlight that category
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={categoryChartData.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(val) => format(new Date(val), 'M/d')}
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <RechartsTooltip
                    content={<SingleLineTooltip highlighted={highlightedCategory} hovered={hoveredLine} />}
                    isAnimationActive={false}
                    cursor={false}
                    wrapperStyle={{ pointerEvents: 'none', visibility: 'visible' }}
                  />
                  <Legend
                    onClick={(e) => setHighlightedCategory(prev => prev === e.value ? null : e.value)}
                    wrapperStyle={{ cursor: 'pointer' }}
                  />
                  {categoryChartData.categories.map((cat, i) => (
                    <Line
                      key={cat}
                      type="monotone"
                      dataKey={cat}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={highlightedCategory === cat ? 5 : 3}
                      strokeOpacity={highlightedCategory === null ? 1 : highlightedCategory === cat ? 1 : 0.1}
                      dot={highlightedCategory === null || highlightedCategory === cat ? { r: 4 } : false}
                      activeDot={{
                        r: 6,
                        onMouseEnter: () => setHoveredLine(cat),
                        onMouseLeave: () => setHoveredLine(null)
                      }}
                      connectNulls
                      onMouseEnter={() => setHoveredLine(cat)}
                      onMouseLeave={() => setHoveredLine(null)}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalObds'}
                    direction={sortColumn === 'totalObds' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalObds')}
                  >
                    OBDs
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalLines'}
                    direction={sortColumn === 'totalLines' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalLines')}
                  >
                    Lines
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalUnits'}
                    direction={sortColumn === 'totalUnits' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalUnits')}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'obdsPerHour'}
                    direction={sortColumn === 'obdsPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('obdsPerHour')}
                  >
                    OBDs/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'linesPerHour'}
                    direction={sortColumn === 'linesPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('linesPerHour')}
                  >
                    Lines/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'unitsPerHour'}
                    direction={sortColumn === 'unitsPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('unitsPerHour')}
                  >
                    Units/Hr
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    No data available for selected date range
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow key={row.category} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.category}</TableCell>
                    <TableCell align="right">{row.totalObds.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalLines.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalUnits.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalHours}</TableCell>
                    <TableCell align="right">{row.obdsPerHour}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {row.linesPerHour}
                    </TableCell>
                    <TableCell align="right">{row.unitsPerHour}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render Associate Performance
  const renderAssociatePerformance = () => {
    const sortedData = sortData(associateData);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Performance by Associate
        </Typography>

        {/* Associate Trendline Chart */}
        {isMultiDay && associateChartData.data.length > 1 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Daily Lines per Hour by Associate
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Click a name in the legend to highlight that associate
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={associateChartData.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(val) => format(new Date(val), 'M/d')}
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <RechartsTooltip
                    content={<SingleLineTooltip highlighted={highlightedAssociate} hovered={hoveredLine} />}
                    isAnimationActive={false}
                    cursor={false}
                    wrapperStyle={{ pointerEvents: 'none', visibility: 'visible' }}
                  />
                  <Legend
                    onClick={(e) => {
                      setHighlightedAssociate(prev => prev === e.value ? null : e.value);
                    }}
                    wrapperStyle={{ cursor: 'pointer' }}
                  />
                  {associateChartData.associates.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={highlightedAssociate === name ? 5 : 3}
                      strokeOpacity={
                        highlightedAssociate === null ? 1 :
                        highlightedAssociate === name ? 1 : 0.1
                      }
                      dot={highlightedAssociate === null || highlightedAssociate === name ? { r: 4 } : false}
                      activeDot={{
                        r: 6,
                        onMouseEnter: () => setHoveredLine(name),
                        onMouseLeave: () => setHoveredLine(null)
                      }}
                      connectNulls
                      onMouseEnter={() => setHoveredLine(name)}
                      onMouseLeave={() => setHoveredLine(null)}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Associate</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Performance</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalObds'}
                    direction={sortColumn === 'totalObds' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalObds')}
                  >
                    OBDs
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalLines'}
                    direction={sortColumn === 'totalLines' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalLines')}
                  >
                    Lines
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalUnits'}
                    direction={sortColumn === 'totalUnits' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalUnits')}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'obdsPerHour'}
                    direction={sortColumn === 'obdsPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('obdsPerHour')}
                  >
                    OBDs/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'linesPerHour'}
                    direction={sortColumn === 'linesPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('linesPerHour')}
                  >
                    Lines/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'unitsPerHour'}
                    direction={sortColumn === 'unitsPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('unitsPerHour')}
                  >
                    Units/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'avgElapsedMinutes'}
                    direction={sortColumn === 'avgElapsedMinutes' ? sortDirection : 'asc'}
                    onClick={() => handleSort('avgElapsedMinutes')}
                  >
                    Avg Elapsed (min)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'avgPauseMinutes'}
                    direction={sortColumn === 'avgPauseMinutes' ? sortDirection : 'asc'}
                    onClick={() => handleSort('avgPauseMinutes')}
                  >
                    Avg Pause (min)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'pctObdsPaused'}
                    direction={sortColumn === 'pctObdsPaused' ? sortDirection : 'asc'}
                    onClick={() => handleSort('pctObdsPaused')}
                  >
                    % OBDs Paused
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                    No data available for selected date range
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.associate}
                    hover
                    sx={{
                      backgroundColor: row.performance === 'top' ? '#e8f5e9' :
                                       row.performance === 'bottom' ? '#ffebee' : 'inherit'
                    }}
                  >
                    <TableCell
                      sx={{ fontWeight: 500, cursor: isMultiDay ? 'pointer' : 'default', '&:hover': isMultiDay ? { textDecoration: 'underline' } : {} }}
                      onClick={() => isMultiDay && setHighlightedAssociate(prev => prev === row.associate ? null : row.associate)}
                    >
                      {row.associate}
                    </TableCell>
                    <TableCell>
                      <PerformanceChip performance={row.performance} />
                    </TableCell>
                    <TableCell align="right">{row.totalObds.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalLines.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalUnits.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalHours}</TableCell>
                    <TableCell align="right">{row.obdsPerHour}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {row.linesPerHour}
                    </TableCell>
                    <TableCell align="right">{row.unitsPerHour}</TableCell>
                    <TableCell align="right">{row.avgElapsedMinutes != null ? row.avgElapsedMinutes : '—'}</TableCell>
                    <TableCell align="right">{row.avgPauseMinutes != null ? row.avgPauseMinutes : '—'}</TableCell>
                    <TableCell align="right">{row.pctObdsPaused != null ? `${row.pctObdsPaused}%` : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {(() => {
          const excludedTotal = associateData.reduce((sum, r) => sum + (r.excludedOutliers || 0), 0);
          return excludedTotal > 0 ? (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              {excludedTotal} outlier{excludedTotal === 1 ? '' : 's'} excluded from Avg Elapsed / Avg Pause / % OBDs Paused
              (elapsed over 8h or multi-assignment time splits). Hours and per-hour rates are unaffected.
            </Typography>
          ) : null;
        })()}
      </Box>
    );
  };

  // Render Associate by Category Performance (expandable)
  const renderAssociateCategoryPerformance = () => {
    const sortedData = sortData(associateCategoryData);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Performance by Associate by Category
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Click on a row to expand and see category breakdown
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ width: 40 }}></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Associate</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalObds'}
                    direction={sortColumn === 'totalObds' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalObds')}
                  >
                    OBDs
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalLines'}
                    direction={sortColumn === 'totalLines' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalLines')}
                  >
                    Lines
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'totalUnits'}
                    direction={sortColumn === 'totalUnits' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalUnits')}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'linesPerHour'}
                    direction={sortColumn === 'linesPerHour' ? sortDirection : 'asc'}
                    onClick={() => handleSort('linesPerHour')}
                  >
                    Lines/Hr
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    No data available for selected date range
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <React.Fragment key={row.associate}>
                    <TableRow
                      hover
                      onClick={() => toggleExpanded(row.associate)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRows.has(row.associate) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.associate}</TableCell>
                      <TableCell align="right">{row.totalObds.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.totalLines.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.totalUnits.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.totalHours}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {row.linesPerHour}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedRows.has(row.associate) ? 1 : 0 }}>
                        <Collapse in={expandedRows.has(row.associate)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 6, pr: 2, backgroundColor: '#fafafa' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Category Breakdown
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>OBDs</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Lines</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Units</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Lines/Hr</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.categories.map((cat) => (
                                  <TableRow key={cat.category}>
                                    <TableCell>{cat.category}</TableCell>
                                    <TableCell align="right">{cat.totalObds}</TableCell>
                                    <TableCell align="right">{cat.totalLines}</TableCell>
                                    <TableCell align="right">{cat.totalUnits}</TableCell>
                                    <TableCell align="right">{cat.totalHours}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                      {cat.linesPerHour}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render Putaway Report — unified across both capture eras:
  // legacy truck-timer sessions (before the cutover) and Activity Monitor
  // receiving check-ins (on/after it), one interleaved table.
  const renderPutawayReport = () => {
    const sessions = putawayData.sessions || [];
    const summary = putawayData.summary || {};
    const cutover = putawayData.cutover_date;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Total Entries
                </Typography>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                  {summary.total_entries || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Total Hours
                </Typography>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                  {summary.total_hours || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  People-Days
                </Typography>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                  {summary.total_people_days || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Unified table — both eras interleaved, sorted by date */}
        {sessions.length === 0 ? (
          <Alert severity="info">No putaway entries found for this date range.</Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>PO</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>People</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{format(new Date(row.date + 'T12:00:00'), 'MMM dd, yyyy')}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.vendor_name}</TableCell>
                      <TableCell>{row.po_number || '-'}</TableCell>
                      <TableCell>{row.people}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.total_hours}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {cutover && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                As of {format(new Date(cutover + 'T12:00:00'), 'MMM dd, yyyy')}, People and Hours reflect individual Activity Monitor check-ins.
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Advanced Reports
        </Typography>

        {/* Date Selection */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="subtitle2" color="textSecondary">
                Quick Filters:
              </Typography>
            </Grid>
            <Grid item>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  variant={activeQuickFilter === 'today' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('today')}
                >
                  Today
                </Button>
                <Button
                  variant={activeQuickFilter === 'yesterday' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('yesterday')}
                >
                  Yesterday
                </Button>
                <Button
                  variant={activeQuickFilter === 'thisWeek' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('thisWeek')}
                >
                  This Week
                </Button>
                <Button
                  variant={activeQuickFilter === 'last7' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('last7')}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant={activeQuickFilter === 'last30' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('last30')}
                >
                  Last 30 Days
                </Button>
                <Button
                  variant={activeQuickFilter === 'thisMonth' ? 'contained' : 'outlined'}
                  onClick={() => handleQuickFilter('thisMonth')}
                >
                  This Month
                </Button>
              </ButtonGroup>
            </Grid>
            <Grid item sx={{ flexGrow: 1 }} />
            <Grid item>
              <DateTimePicker
                label="Start Date/Time (ET)"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && isValid(newValue)) {
                    setStartDate(newValue);
                    setActiveQuickFilter(null);
                  }
                }}
                slotProps={{
                  textField: { size: 'small', sx: { width: 220 } },
                  actionBar: { actions: ['clear', 'accept'] }
                }}
                ampm={true}
              />
            </Grid>
            <Grid item>
              <DateTimePicker
                label="End Date/Time (ET)"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && isValid(newValue)) {
                    setEndDate(newValue);
                    setActiveQuickFilter(null);
                  }
                }}
                slotProps={{
                  textField: { size: 'small', sx: { width: 220 } },
                  actionBar: { actions: ['clear', 'accept'] }
                }}
                ampm={true}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            All times are in Eastern timezone (America/New_York). Default shift: 6:00 PM - 2:00 AM.
          </Typography>
        </Paper>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Report Tabs */}
        {!loading && (
          <>
            <Paper sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Building Overview" />
                <Tab label="By Category" />
                <Tab label="By Associate" />
                <Tab label="Associate by Category" />
                <Tab label="Putaway" icon={<LocalShippingIcon />} iconPosition="start" />
              </Tabs>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{ mr: 2 }}
                disabled={
                  (activeTab === 0 && !summary) ||
                  (activeTab === 1 && !categoryData.length) ||
                  (activeTab === 2 && !associateData.length) ||
                  (activeTab === 3 && !associateCategoryData.length) ||
                  (activeTab === 4 && !putawayData.sessions?.length)
                }
              >
                Export CSV
              </Button>
            </Paper>

            <Paper sx={{ p: 3 }}>
              {activeTab === 0 && renderBuildingOverview()}
              {activeTab === 1 && renderCategoryPerformance()}
              {activeTab === 2 && renderAssociatePerformance()}
              {activeTab === 3 && renderAssociateCategoryPerformance()}
              {activeTab === 4 && renderPutawayReport()}
            </Paper>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default Reports;
